/* LumaSpec catalog behaviour (CATALOG lane): filters / sort / pages re-render the list section in place (Section
   Rendering API), grid <-> spec-table view, table column sort, and compare up to 4 products (bar + dialog with the
   full spec rows from sections/ls-collection-compare-data.liquid). Loaded as a module by the catalog sections and by
   snippets/ls-product-card.liquid (the browser runs a module once per page). Everything degrades to plain links and
   GET forms without it. */

const CMP_KEY = 'ls-compare';
const VIEW_KEY = 'ls-view';
const MAX = 4;

const store = {
  get(key, fallback) {
    try {
      const v = localStorage.getItem(key);
      return v === null ? fallback : JSON.parse(v);
    } catch {
      return fallback;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* private mode: compare still works for this page */
    }
  },
};

/* Interface strings: snippets/ls-catalog-i18n.liquid (locales ls.catalog.compare; placeholders {n} in single braces so
   Liquid's t filter leaves them for this script), English defaults otherwise. */
const STR = {
  bar: 'Compare {n}/{max}', clear: 'Clear', remove: 'Remove {name}', open: 'Compare',
  title: 'Compare {n} products', diff_only: 'Differences only', close: 'Close', loading: 'Loading specs\u2026',
  price: 'Price', availability: 'Availability', spec: 'Spec',
};
try {
  Object.assign(STR, JSON.parse(document.querySelector('[data-ls-catalog-i18n]')?.textContent || '{}'));
} catch {
  /* keep the defaults */
}

/* the t filter HTML-escapes plain (non-_html) keys: back to plain text once (shown via textContent / esc()) */
for (const k of Object.keys(STR)) {
  if (typeof STR[k] === 'string' && STR[k].includes('&')) {
    const d = document.createElement('textarea');
    d.innerHTML = STR[k];
    STR[k] = d.value;
  }
}
/** @param {keyof typeof STR} key @param {Record<string, string|number>} [vars] */
const tr = (key, vars = {}) => String(STR[key] ?? key).replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ''));

const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/* ---------------- view: grid / table ---------------- */

function currentView() {
  const v = store.get(VIEW_KEY, null);
  return v === 'table' || v === 'grid' ? v : null;
}

function applyView(root = document) {
  const saved = currentView();
  for (const res of root.querySelectorAll('[data-ls-results]')) {
    if (saved) res.dataset.view = saved;
    const view = res.dataset.view;
    const section = res.closest('[data-ls-section]') || document;
    for (const b of section.querySelectorAll('[data-ls-view-btn]')) {
      b.setAttribute('aria-pressed', String(b.dataset.lsViewBtn === view));
    }
  }
}

/* ---------------- table sort ---------------- */

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

function sortTable(button) {
  const th = button.closest('th');
  const table = button.closest('table');
  if (!th || !table || !table.tBodies[0]) return;
  const index = [...th.parentElement.children].indexOf(th);
  const dir = th.getAttribute('aria-sort') === 'ascending' ? 'descending' : 'ascending';
  for (const other of th.parentElement.children) other.removeAttribute('aria-sort');
  th.setAttribute('aria-sort', dir);
  const numeric = button.dataset.lsSort === 'num';
  const value = (row) => row.children[index]?.dataset.v ?? '';
  const rows = [...table.tBodies[0].rows];
  rows.sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    if (va === '' && vb !== '') return 1; // blanks last either way
    if (vb === '' && va !== '') return -1;
    const r = numeric ? parseFloat(va) - parseFloat(vb) : collator.compare(va, vb);
    return dir === 'ascending' ? r : -r;
  });
  table.tBodies[0].append(...rows);
}

/* ---------------- section re-render (filters, sort, pages) ---------------- */

let inflight = null;

/* Legacy resolution filter: keep bookmarked links after the group label changes. */
function canonicalResolutionFilterUrl(href, base = location.href) {
  try {
    const url = new URL(href, base);
    if (url.origin !== new URL(base).origin || !url.pathname.includes('/collections/')) return null;
    const key = 'filter.p.m.lsv.f_resolution';
    const values = url.searchParams.getAll(key);
    if (!values.includes('29 MP +')) return null;
    url.searchParams.delete(key);
    values.forEach(value => url.searchParams.append(key, value === '29 MP +' ? 'Over 25 MP' : value));
    return url;
  } catch {
    return null;
  }
}
function alternateSearchResolutionUrl(href, nativeCount, base = location.href) {
  try {
    if (nativeCount == null || String(nativeCount).trim() === '' || Number(nativeCount) !== 0) return null;
    const url = new URL(href, base);
    if (url.origin !== new URL(base).origin || !/\/search\/?$/.test(url.pathname)) return null;
    const key = 'filter.p.m.lsv.f_resolution';
    const values = url.searchParams.getAll(key);
    const old = values.includes('29 MP +'), current = values.includes('Over 25 MP');
    if (old === current) return null;
    const from = old ? '29 MP +' : 'Over 25 MP';
    const to = old ? 'Over 25 MP' : '29 MP +';
    url.searchParams.delete(key);
    values.forEach(value => url.searchParams.append(key, value === from ? to : value));
    return url;
  } catch {
    return null;
  }
}

function combinedSearchResolutionUrl(href, operator, base = location.href) {
  try {
    const url = new URL(href, base);
    if (operator !== 'OR' || url.origin !== new URL(base).origin || !/\/search\/?$/.test(url.pathname)) return null;
    const key = 'filter.p.m.lsv.f_resolution';
    const values = url.searchParams.getAll(key);
    const old = values.includes('29 MP +'), current = values.includes('Over 25 MP');
    if (!old && !current) return null;
    const combined = values.filter(value => value !== '29 MP +' && value !== 'Over 25 MP').concat(['29 MP +', 'Over 25 MP']);
    if (values.length === combined.length && values.every((value, index) => value === combined[index])) return null;
    url.searchParams.delete(key);
    combined.forEach(value => url.searchParams.append(key, value));
    return url;
  } catch {
    return null;
  }
}

async function probeSearchResolution(section, url, signal) {
  const alternate = combinedSearchResolutionUrl(url, section.dataset.lsResolutionOperator) || alternateSearchResolutionUrl(url, section.dataset.lsNativeCount);
  if (!alternate || !section.dataset.sectionId) return null;
  const request = new URL(alternate);
  request.searchParams.set('section_id', section.dataset.sectionId);
  try {
    const response = await fetch(request, { signal });
    if (!response.ok) return null;
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const fresh = doc.querySelector('[data-ls-section]');
    return fresh && Number(fresh.dataset.lsNativeCount) > 0 ? { url: alternate, fresh } : null;
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    return null;
  }
}
/* End legacy resolution filter. */

/** @param {HTMLElement} section @param {string} href */
async function renderSection(section, href, push = true) {
  const id = section.dataset.sectionId;
  if (!id) return void (location.href = href);
  const canonical = canonicalResolutionFilterUrl(href);
  let url = canonical || combinedSearchResolutionUrl(href, section.dataset.lsResolutionOperator) || new URL(href, location.href);
  if (canonical && !push) history.replaceState(history.state, '', url);
  const fetchUrl = new URL(url);
  fetchUrl.searchParams.set('section_id', id);
  inflight?.abort();
  const ctrl = new AbortController();
  inflight = ctrl;
  section.setAttribute('aria-busy', 'true');
  const focusId = document.activeElement?.id;
  const drawerOpen = section.querySelector('[data-ls-fdrawer]')?.open;
  try {
    const res = await fetch(fetchUrl, { signal: ctrl.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    let fresh = doc.querySelector('[data-ls-section]');
    if (!fresh) throw new Error('section missing');
    const alternative = await probeSearchResolution(fresh, url, ctrl.signal);
    if (alternative) ({ url, fresh } = alternative);
    if (ctrl !== inflight) return;
    section.replaceChildren(...fresh.childNodes);
    if (fresh.dataset.lsNativeCount !== undefined) section.dataset.lsNativeCount = fresh.dataset.lsNativeCount;
    if (fresh.dataset.lsResolutionOperator !== undefined) section.dataset.lsResolutionOperator = fresh.dataset.lsResolutionOperator;
    const drawer = section.querySelector('[data-ls-fdrawer]');
    if (drawer && drawerOpen !== undefined) drawer.open = drawerOpen;
    if (push) history.pushState({ lsSection: id }, '', url);
    else if (url.href !== location.href) history.replaceState(history.state, '', url);
    applyView(section);
    syncChecks();
    liveForms(section);
    const again = focusId && document.getElementById(focusId);
    if (again) again.focus({ preventScroll: true });
    else if (!drawerOpen || matchMedia('(min-width: 990px)').matches) {
      const top = section.getBoundingClientRect().top;
      if (top < 0) section.scrollIntoView({ block: 'start' });
    }
  } catch (err) {
    if (err?.name !== 'AbortError') location.href = url.href; // fall back to a normal page load
  } finally {
    if (ctrl === inflight) section.removeAttribute('aria-busy');
  }
}

function formUrl(form) {
  const url = new URL(form.getAttribute('action') || location.pathname, location.href);
  const params = new URLSearchParams();
  for (const [k, v] of new FormData(form)) if (String(v).trim() !== '') params.append(k, String(v));
  url.search = params.toString();
  return (combinedSearchResolutionUrl(url, form.dataset.lsResolutionOperator) || url).href;
}

function mergeResolutionFilters(form) {
  if (form.dataset.lsResolutionMerged || form.dataset.lsResolutionOperator !== 'OR' || !form.closest('[data-ls-native-count]')) return;
  form.dataset.lsResolutionMerged = 'true';
  const inputs = [...form.querySelectorAll('input[data-ls-resolution-family]')];
  if (!inputs.length) return;
  const main = inputs[0];
  const active = inputs.some(input => input.checked);
  const count = inputs.reduce((n, input) => n + (Number(input.closest('label')?.querySelector('.ls-check__n')?.textContent) || 0), 0);
  main.checked = active;
  main.disabled = !count && !active;
  main.closest('label')?.classList.toggle('is-off', main.disabled);
  const badge = main.closest('label')?.querySelector('.ls-check__n');
  if (badge) badge.textContent = String(count);
  inputs.slice(1).forEach(input => {
    input.disabled = true;
    input.checked = false;
    input.closest('li').hidden = true;
  });
  const pills = [...form.querySelectorAll('a[data-ls-resolution-remove]')];
  if (!pills.length) return;
  const key = 'filter.p.m.lsv.f_resolution';
  const clear = new URL(pills[0].href, location.href);
  const remaining = clear.searchParams.getAll(key).filter(value => value !== '29 MP +' && value !== 'Over 25 MP');
  clear.searchParams.delete(key);
  remaining.forEach(value => clear.searchParams.append(key, value));
  pills[0].href = clear.href;
  pills.slice(1).forEach(pill => { pill.closest('li').hidden = true; });
  for (const node of [form.querySelector('.ls-filters__head .ls-count'), main.closest('details')?.querySelector('.ls-filter__sum .ls-count')]) {
    if (node) node.textContent = String(Number(node.textContent) - Math.max(0, pills.length - 1));
  }
}

function liveForms(root = document) {
  for (const f of root.querySelectorAll('[data-ls-filters]')) {
    f.classList.add('is-live');
    mergeResolutionFilters(f);
  }
}

let priceTimer = 0;

function onFormChange(event) {
  const el = /** @type {HTMLElement} */ (event.target);
  const form = el.closest('form') || (el instanceof HTMLSelectElement && el.form);
  if (!form) return;
  const isPrice = el instanceof HTMLInputElement && el.type === 'number';
  const section = form.closest('[data-ls-section]') || el.closest('[data-ls-section]');
  if (!section || !(form.matches('[data-ls-filters]') || el.matches('[data-ls-sortby]') || form.id.endsWith('-form'))) return;
  clearTimeout(priceTimer);
  const go = () => renderSection(section, formUrl(form));
  if (isPrice && event.type === 'input') priceTimer = setTimeout(go, 700);
  else if (!isPrice || event.type === 'change') go();
}

/* ---------------- compare ---------------- */

/** @returns {{handle:string,title:string,model:string,url:string,img:string,price:string}[]} */
const picked = () => store.get(CMP_KEY, []).filter((p) => p && p.handle).slice(0, MAX);

function syncChecks() {
  const set = new Set(picked().map((p) => p.handle));
  const full = set.size >= MAX;
  for (const box of document.querySelectorAll('input[data-ls-compare]')) {
    box.checked = set.has(box.value);
    box.disabled = full && !box.checked;
  }
  renderBar();
}

function toggle(box) {
  let list = picked();
  if (box.checked) {
    if (list.length >= MAX) {
      box.checked = false;
      return;
    }
    const card = box.closest('[data-ls-card]');
    const d = card ? card.dataset : {};
    list.push({ handle: box.value, title: d.title || box.value, model: d.model || '', url: d.url || '', img: d.img || '', price: d.price || '' });
  } else {
    list = list.filter((p) => p.handle !== box.value);
  }
  store.set(CMP_KEY, list);
  syncChecks();
}

let bar = null;

function renderBar() {
  const list = picked();
  if (!list.length) {
    if (bar) bar.hidden = true;
    return;
  }
  if (!bar) {
    bar = document.createElement('div');
    bar.className = 'ls-cmpbar';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', tr('open'));
    document.body.append(bar);
  }
  bar.hidden = false;
  bar.innerHTML =
    `<span class="ls-cmpbar__label">${esc(tr('bar', { n: list.length, max: MAX }))}</span><ul class="ls-cmpbar__items">${list
      .map(
        (p) =>
          `<li>${p.img ? `<img src="${esc(p.img)}" alt="${esc(p.model || p.title)}" width="44" height="44">` : `<span class="ls-cmpbar__ph"></span>`}` +
          `<button type="button" class="ls-cmpbar__x" data-ls-cmp-remove="${esc(p.handle)}" aria-label="${esc(tr('remove', { name: p.model || p.title }))}">&times;</button></li>`
      )
      .join('')}</ul><button type="button" class="ls-cmpbar__clear" data-ls-cmp-clear>${esc(tr('clear'))}</button>` +
    `<button type="button" class="ls-btn ls-btn--primary ls-btn--sm ls-cmpbar__go" data-ls-cmp-open${list.length < 2 ? ' disabled' : ''}>${esc(tr('open'))}</button>`;
}

async function compareData(p) {
  const url = new URL(p.url || `/products/${p.handle}`, location.href);
  url.searchParams.set('section_id', 'ls-collection-compare-data');
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
  const json = doc.querySelector('[data-ls-compare-data]')?.textContent;
  return json ? JSON.parse(json) : { ...p, specs: [] };
}

async function openCompare() {
  const list = picked();
  if (list.length < 2) return;
  let dlg = /** @type {HTMLDialogElement|null} */ (document.querySelector('.ls-cmpdlg'));
  if (!dlg) {
    dlg = document.createElement('dialog');
    dlg.className = 'ls-cmpdlg';
    dlg.setAttribute('aria-labelledby', 'ls-cmpdlg-h');
    dlg.addEventListener('click', (e) => {
      if (e.target === dlg) dlg.close(); // backdrop click
    });
    document.body.append(dlg);
  }
  dlg.innerHTML = `<div class="ls-cmpdlg__head"><h2 id="ls-cmpdlg-h">${esc(tr('title', { n: list.length }))}</h2>
    <label class="ls-cmpdlg__opts"><input type="checkbox" data-ls-cmp-diff> ${esc(tr('diff_only'))}</label>
    <button type="button" class="ls-cmpdlg__close" data-ls-cmp-close aria-label="${esc(tr('close'))}">&times;</button></div>
    <div class="ls-cmpdlg__body" role="status"><p style="padding:24px">${esc(tr('loading'))}</p></div>`;
  dlg.showModal();
  const items = await Promise.all(list.map((p) => compareData(p).catch(() => ({ ...p, specs: [] }))));
  // rows line up across product families: "Voltage (V)" and "Voltage" are one row (key = label without its unit,
  // the unit moves onto bare numbers), and internal datasheet rows are left out (UX audit 2026-10-23)
  const unitOf = (l) => (l.match(/\(([^()]{1,8})\)\s*$/) || [])[1] || '';
  const keyOf = (l) => l.toLowerCase().replace(/\s*\([^()]{1,8}\)\s*$/, '').replace(/[^a-z0-9\u00c0-\uffff]+/g, ' ').trim();
  const SKIP = /^(model|model code|model no|model number|part no|part number|sku|order code|illumination (chart|curve|distribution).*)$/;
  const labels = [];
  const shown = new Map();
  const maps = items.map((it) => {
    const m = new Map();
    for (const row of Array.isArray(it.specs) ? it.specs : []) {
      if (!Array.isArray(row) || row.length < 2) continue;
      const label = String(row[0]);
      const en = String(row[2] ?? label); // translated rows carry the English label 3rd
      const key = keyOf(en);
      if (!key || SKIP.test(key)) continue;
      let val = String(row[1]);
      const unit = unitOf(label);
      if (unit && /^[\d.,\-~x\u00b1 ]+$/.test(val.trim())) val = `${val.trim()} ${unit}`;
      if (!m.has(key)) m.set(key, val);
      if (!shown.has(key)) shown.set(key, label.replace(/\s*\([^()]{1,8}\)\s*$/, ''));
      if (!labels.includes(key)) labels.push(key);
    }
    return m;
  });
  const cell = (v) => `<td>${v ? esc(v) : '<span class="ls-t__na">-</span>'}</td>`;
  const row = (label, values) => {
    // a difference = at least two products print this row with different values (a missing row is not one)
    const set = new Set(values.map((v) => (v || '').trim().toLowerCase()).filter(Boolean));
    return `<tr class="${set.size > 1 ? 'is-diff' : 'is-same'}"><th scope="row">${esc(label)}</th>${values.map(cell).join('')}</tr>`;
  };
  const head = items
    .map(
      (it) =>
        `<td>${it.img ? `<img src="${esc(it.img)}" alt="" width="120" height="120" loading="lazy">` : ''}` +
        `<a href="${esc(it.url)}">${esc(it.model || it.title)}</a><div class="ls-muted" style="font-size:12px">${esc(it.model ? it.title : '')}</div></td>`
    )
    .join('');
  const body =
    row(tr('price'), items.map((it) => it.price)) +
    row(tr('availability'), items.map((it) => it.stock)) +
    labels.map((l) => row(shown.get(l), maps.map((m) => m.get(l) || ''))).join('');
  const box = dlg.querySelector('.ls-cmpdlg__body');
  box.removeAttribute('role');
  box.innerHTML = `<table class="ls-cmpt"><thead><tr><th scope="col"><span class="ls-sr">${esc(tr('spec'))}</span></th>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

/* ---------------- wiring (once per page) ---------------- */

if (!window.__lsCatalog) {
  window.__lsCatalog = true;

  document.addEventListener('change', (e) => {
    const t = /** @type {HTMLElement} */ (e.target);
    if (t.matches('input[data-ls-compare]')) return toggle(t);
    if (t.matches('[data-ls-cmp-diff]')) {
      t.closest('dialog')?.querySelector('.ls-cmpt')?.classList.toggle('only-diff', /** @type {HTMLInputElement} */ (t).checked);
      return;
    }
    if (t.closest('[data-ls-section]')) onFormChange(e);
  });

  document.addEventListener('input', (e) => {
    const t = /** @type {HTMLElement} */ (e.target);
    if (t instanceof HTMLInputElement && t.type === 'number' && t.closest('[data-ls-filters]')) onFormChange(e);
  });

  document.addEventListener('submit', (e) => {
    const form = /** @type {HTMLFormElement} */ (e.target);
    const section = form.closest('[data-ls-section]');
    if (!section || !form.matches('[data-ls-filters]')) return;
    e.preventDefault();
    if (matchMedia('(max-width: 989px)').matches) {
      const drawer = section.querySelector('[data-ls-fdrawer]');
      if (drawer) drawer.open = false;
    }
    // Capture the closed state before the async render preserves it on the replacement drawer.
    renderSection(section, formUrl(form));
  });

  document.addEventListener('click', (e) => {
    const t = /** @type {HTMLElement} */ (e.target);
    const viewBtn = t.closest('[data-ls-view-btn]');
    if (viewBtn) {
      store.set(VIEW_KEY, viewBtn.dataset.lsViewBtn);
      applyView();
      return;
    }
    const sortBtn = t.closest('[data-ls-sort]');
    if (sortBtn) return sortTable(sortBtn);
    const nav = t.closest('a[data-ls-nav]');
    if (nav && !(e instanceof MouseEvent && (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0))) {
      const section = nav.closest('[data-ls-section]');
      if (section) {
        e.preventDefault();
        renderSection(section, nav.href);
      }
      return;
    }
    const rm = t.closest('[data-ls-cmp-remove]');
    if (rm) {
      store.set(CMP_KEY, picked().filter((p) => p.handle !== rm.dataset.lsCmpRemove));
      return syncChecks();
    }
    if (t.closest('[data-ls-cmp-clear]')) {
      store.set(CMP_KEY, []);
      return syncChecks();
    }
    if (t.closest('[data-ls-cmp-open]')) return void openCompare();
    if (t.closest('[data-ls-cmp-close]')) return t.closest('dialog')?.close();
  });

  window.addEventListener('popstate', () => {
    const section = document.querySelector('[data-ls-section]');
    if (section && (section.dataset.lsNativeCount !== undefined || section.querySelector('[data-ls-results], .ls-empty'))) renderSection(section, location.href, false);
  });

  window.addEventListener('storage', (e) => {
    if (e.key === CMP_KEY) syncChecks();
  });

  // POLISH lane: cards inserted after load (series image grid, sections/ls-series-grid.liquid) get their ticks
  document.addEventListener('ls:cards-added', syncChecks);

  const init = () => {
    const canonical = canonicalResolutionFilterUrl(location.href);
    if (canonical) return void location.replace(canonical.href);
    applyView();
    liveForms();
    syncChecks();
    const section = document.querySelector('[data-ls-section]');
    const href = location.href;
    const combined = section && combinedSearchResolutionUrl(href, section.dataset.lsResolutionOperator);
    if (combined) return void location.replace(combined.href);
    if (section && alternateSearchResolutionUrl(href, section.dataset.lsNativeCount)) {
      inflight?.abort();
      const ctrl = new AbortController();
      inflight = ctrl;
      probeSearchResolution(section, new URL(href), ctrl.signal).then(alternative => {
        if (alternative && ctrl === inflight && location.href === href) {
          location.replace((combinedSearchResolutionUrl(alternative.url, alternative.fresh.dataset.lsResolutionOperator) || alternative.url).href);
        }
      }).catch(() => {});
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
}
